import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, ArrowRight, FileDown } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email });
      setDone(true);
      setEmail("");
      toast.success("You're on the list.", {
        description: "Thanks — we'll share Ecospora updates when there's something worth sharing.",
      });
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-ecospora-green-deep text-ecospora-cream" data-testid="site-footer">
      <div className="container-x py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="font-display text-3xl font-bold tracking-tight">Ecospora</div>
            <p className="mt-5 text-ecospora-cream/70 max-w-sm text-[15px] leading-relaxed">
              Mushrooms as a platform. For food, materials, employment, and environmental return.
            </p>
            <address className="not-italic mt-8 text-sm text-ecospora-cream/60 leading-relaxed">
              Ecospora Sdn. Bhd.<br />
              Perlis, Malaysia<br />
              <a href="mailto:hello@ecospora.com" className="hover:text-ecospora-ochre transition-colors">
                hello@ecospora.com
              </a>
            </address>
            <p className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ecospora-ochre">
              <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-ecospora-ochre" />
              Rooted in Perlis
            </p>
            <div className="flex items-center gap-4 mt-8">
              <a href="#" className="w-10 h-10 rounded-full border border-ecospora-cream/20 flex items-center justify-center hover:bg-ecospora-cream/10 transition" data-testid="social-instagram" aria-label="Instagram">
                <Instagram size={16} strokeWidth={1.5} />
              </a>
              <a href="#" className="w-10 h-10 rounded-full border border-ecospora-cream/20 flex items-center justify-center hover:bg-ecospora-cream/10 transition" data-testid="social-linkedin" aria-label="LinkedIn">
                <Linkedin size={16} strokeWidth={1.5} />
              </a>
              <a href="#" className="w-10 h-10 rounded-full border border-ecospora-cream/20 flex items-center justify-center hover:bg-ecospora-cream/10 transition" data-testid="social-facebook" aria-label="Facebook">
                <Facebook size={16} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Site links */}
          <div className="md:col-span-3">
            <h3 className="text-xs uppercase tracking-[0.18em] text-ecospora-ochre mb-6 font-semibold">Explore</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="hover:text-ecospora-ochre transition">Home</Link></li>
              <li><Link to="/impact" className="hover:text-ecospora-ochre transition">Impact</Link></li>
              <li><Link to="/collaborate" className="hover:text-ecospora-ochre transition">Collaborate</Link></li>
              <li><Link to="/contact" className="hover:text-ecospora-ochre transition">Contact</Link></li>
              <li className="pt-4">
                <a href="#" className="hover:text-ecospora-ochre transition">KukurMas ↗</a>
              </li>
              <li>
                <a href="#" className="hover:text-ecospora-ochre transition">Cendawan Hub ↗</a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-4">
            <h3 className="text-xs uppercase tracking-[0.18em] text-ecospora-ochre mb-6 font-semibold">Stay in the loop</h3>
            <p className="text-sm text-ecospora-cream/70 mb-5 leading-relaxed">
              Quarterly updates on the platform, partnerships, and what's growing next.
            </p>
            <form onSubmit={onSubscribe} className="flex flex-col gap-3" data-testid="newsletter-form">
              <div className="flex gap-2 border-b border-ecospora-cream/30 focus-within:border-ecospora-ochre transition-colors pb-1">
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent text-ecospora-cream placeholder:text-ecospora-cream/40 py-3 focus:outline-none text-sm"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="text-ecospora-ochre hover:text-ecospora-cream transition disabled:opacity-50"
                  data-testid="newsletter-submit-btn"
                  aria-label="Subscribe"
                >
                  <ArrowRight size={20} strokeWidth={1.5} />
                </button>
              </div>
              {done && (
                <p className="text-xs text-ecospora-green-soft" data-testid="newsletter-success">
                  Subscribed. Welcome aboard.
                </p>
              )}
            </form>

            <a
              href="#"
              className="mt-8 inline-flex items-center gap-2 text-sm text-ecospora-cream/70 hover:text-ecospora-ochre transition"
              data-testid="impact-report-download"
            >
              <FileDown size={16} strokeWidth={1.5} />
              Download our Impact Report
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-ecospora-cream/10">
        <div className="container-x py-6 flex flex-col md:flex-row gap-3 md:gap-6 items-start md:items-center justify-between text-xs text-ecospora-cream/50">
          <div>© {new Date().getFullYear()} Ecospora Sdn. Bhd. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-ecospora-ochre transition">Privacy</a>
            <a href="#" className="hover:text-ecospora-ochre transition">Terms</a>
            <span>EN / BM</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
